import { useMutation } from '@apollo/client';
import { deleteExperimentVariantMutation } from 'api/mutations/experiments';
import { updateExperimentEditVariants } from 'modules/experiments/takeover/ducks/actions';
import { DeleteExperimentVariantProps, MutateVariant } from 'modules/experiments/types';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useExperimentsTranslations } from '../useTranslations';

const source = 'useDeleteExperimentVariant';

export const useDeleteExperimentVariant = ({
  experimentId,
  showErrorToaster = true,
  showSuccessToaster = true,
}: MutateVariant) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const errorMessage = `Failed to delete variant for experiment id ${experimentId}`;
  const errorToasterMessage = t('errorMessage.deleteVariant');

  const errorHandler = (error: unknown, additional = {}) => {
    if (showErrorToaster)
      dispatch(
        addNegativeToast(
          errorToasterMessage,
          errorIdentifier({
            errorMessage,
            source,
            error,
            additional,
          }),
        ),
      );
  };

  const [deleteExperimentVariant] = useMutation(deleteExperimentVariantMutation, {
    onError: (e) => errorHandler(e),
  });

  return async (
    { variantId, variantName, onSuccess }: DeleteExperimentVariantProps,
    locale: string,
    updateRedux = true,
  ): Promise<boolean> => {
    const { data } = await deleteExperimentVariant({
      variables: {
        experimentId,
        variationId: variantId,
        ...(locale && { locale }),
      },
    });

    if (data?.deleteVariation?.variations) {
      if (showSuccessToaster) {
        const successToasterMessage = t('successMessage.deleteVariant', { variantName });
        dispatch(addPositiveToast(successToasterMessage));
      }

      if (onSuccess) await onSuccess();

      // We need to save the original BE variant object to the redux state
      // We have selectors which will mutate this date when a component needs it
      if (updateRedux) {
        dispatch(updateExperimentEditVariants(data.deleteVariation.variations));
      }
      return true;
    }

    if (data?.deleteVariation?.message) {
      errorHandler(data.deleteVariation.message, { variantId });
    }
    return false;
  };
};
